import type { PublicClientChannelState, PublicOptionalClientChannelKey } from '../api/bookingApi.js';
import { useTranslation } from '../../i18n/useTranslation';
import { AppIcon } from '../ui/Icon/AppIcon.tsx';

type Props = {
  channels: PublicClientChannelState[];
  clientEmail?: string | null;
  pendingChannel?: PublicOptionalClientChannelKey | null;
  pendingEmailRemoval?: boolean;
  onToggle: (channel: PublicOptionalClientChannelKey, enabled: boolean) => void;
  onEmailRemove?: () => void;
};

type ActionButtonProps = {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
};

/** Одна ширина для «Написать …» и «Отвязать» */
const channelActionStyle = {
  width: '220px',
  maxWidth: '100%',
  boxSizing: 'border-box' as const,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center' as const,
  padding: 'var(--spacing-sm) var(--spacing-md)',
  fontSize: 'var(--font-size-sm)',
};

function ActionButton({ label, disabled, onClick, variant = 'primary' }: ActionButtonProps) {
  const cls =
    variant === 'secondary'
      ? 'booking-button booking-button-secondary'
      : 'booking-button booking-button-primary';
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      className={cls}
      style={{ ...channelActionStyle, opacity: disabled ? 0.6 : 1 }}
    >
      {label}
    </button>
  );
}

export function ClientNotificationChannelsCard({
  channels,
  clientEmail,
  pendingChannel,
  pendingEmailRemoval,
  onToggle,
  onEmailRemove,
}: Props) {
  const { t } = useTranslation();
  const channelByKey = new Map(channels.map((channel) => [channel.channel, channel]));
  const email = channelByKey.get('email');
  const vk = channelByKey.get('vk');
  const telegram = channelByKey.get('telegram');

  const renderChannelControl = (
    channel: PublicClientChannelState | undefined,
    connectHref: string | null,
    connectLabel: string
  ) => {
    if (!channel) {
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    }

    if (channel.connected) {
      const key = channel.channel;
      if (key === 'vk' || key === 'telegram') {
        return (
          <ActionButton
            variant="secondary"
            label={t('booking.success.detachChannel')}
            disabled={pendingChannel === key || !channel.available}
            onClick={() => onToggle(key, false)}
          />
        );
      }
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    }

    if (!channel.available || !connectHref) {
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    }

    return (
      <a
        href={connectHref}
        target="_blank"
        rel="noopener noreferrer"
        className="booking-button booking-button-primary"
        style={{
          ...channelActionStyle,
          textDecoration: 'none',
        }}
      >
        {connectLabel}
      </a>
    );
  };

  const renderEmailControl = (channel: PublicClientChannelState | undefined) => {
    if (!channel?.connected || !clientEmail || !onEmailRemove) {
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    }

    return (
      <button
        type="button"
        className="booking-icon-button-plain"
        disabled={pendingEmailRemoval}
        onClick={() => !pendingEmailRemoval && onEmailRemove()}
        aria-label={t('booking.success.deleteChannel')}
        title={t('booking.success.deleteChannel')}
      >
        <AppIcon name="trash" size={20} style={{ color: 'currentColor' }} />
      </button>
    );
  };

  return (
    <div className="booking-manage-master" style={{ textAlign: 'left' }}>
      <div className="booking-manage-master-title">{t('booking.success.notificationChannelsTitle')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        <div className="booking-flex-between" style={{ alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className="booking-manage-summary-label">{t('booking.success.email')}:</span>
            {email?.connected && email.enabled && (
              <span className="booking-text-sm booking-color-muted">{t('booking.success.alwaysOn')}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span style={{ textAlign: 'right' }}>{clientEmail || '—'}</span>
            {renderEmailControl(email)}
          </div>
        </div>
        <div className="booking-flex-between" style={{ alignItems: 'center' }}>
          <span className="booking-manage-summary-label">{t('booking.success.vkontakte')}:</span>
          {renderChannelControl(vk, vk?.connectUrl ?? null, t('booking.success.writeToVkShort'))}
        </div>
        <div className="booking-flex-between" style={{ alignItems: 'center' }}>
          <span className="booking-manage-summary-label">{t('booking.success.telegram')}:</span>
          {renderChannelControl(telegram, telegram?.connectUrl ?? null, t('booking.success.writeToTelegramShort'))}
        </div>
      </div>
    </div>
  );
}

